class GrHeaderScroll extends HTMLElement {
  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.#header = this.querySelector('.gr-header') || this.closest('.gr-header') || this;
    this.#drawer = this.querySelector('gr-header-drawer');
    this.#mediaQuery = window.matchMedia('(min-width: 990px)');
    this.#lastY = this.#getScrollTop();
    this.#hidden = this.#header?.hasAttribute('data-scroll-hidden') ?? false;

    this.#bindScrollTarget();
    this.#mediaQuery.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    this.#unbindScrollTarget();
    this.#mediaQuery?.removeEventListener('change', this.#onBreakpointChange);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#bound = false;
  }

  #bound = false;
  /** @type {HTMLElement | null} */
  #header = null;
  /** @type {HTMLElement | null} */
  #drawer = null;
  /** @type {MediaQueryList | null} */
  #mediaQuery = null;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #lastY = 0;
  #rafId = null;
  #hidden = false;
  #lastToggleAt = 0;
  /** Ignore scroll near top — keep header visible */
  #topThreshold = 48;
  /** Require clearer downward movement before hiding */
  #hideDelta = 16;
  /** Slightly easier to reveal on scroll up */
  #showDelta = 10;
  /** Ignore tiny rubber-band / momentum noise */
  #noiseDelta = 2;
  /** Prevent rapid flip-flop while momentum settles */
  #cooldownMs = 180;

  #getScrollTop() {
    if (this.#mediaQuery?.matches) {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) return wrapper.scrollTop;
    }
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  #bindScrollTarget() {
    this.#unbindScrollTarget();
    this.#scrollTarget = this.#mediaQuery?.matches
      ? document.querySelector('.page-wrapper') || window
      : window;
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #unbindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
  }

  #onBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#lastY = this.#getScrollTop();
    this.#setHidden(false);
  };

  #onScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#rafId = null;

    if (this.#drawer?.hasAttribute('open')) {
      this.#setHidden(false);
      this.#lastY = this.#getScrollTop();
      return;
    }

    const y = this.#getScrollTop();
    const delta = y - this.#lastY;

    // Drop micro jitter / iOS rubber-band noise without updating direction baseline
    if (Math.abs(delta) < this.#noiseDelta) return;

    const now = performance.now();
    if (now - this.#lastToggleAt < this.#cooldownMs) {
      this.#lastY = y;
      return;
    }

    if (y <= this.#topThreshold) {
      this.#setHidden(false);
    } else if (delta > this.#hideDelta) {
      this.#setHidden(true);
    } else if (delta < -this.#showDelta) {
      this.#setHidden(false);
    }

    this.#lastY = y;
  };

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (this.#hidden === hidden) return;
    this.#hidden = hidden;
    this.#lastToggleAt = performance.now();
    this.#header?.toggleAttribute('data-scroll-hidden', hidden);
  }
}

if (!customElements.get('gr-header-scroll')) {
  customElements.define('gr-header-scroll', GrHeaderScroll);
}
